import {Row, Col, Form, Input, Card, Button, Select, Divider, Switch, Grid} from "antd";
import DatePicker from "../../../core/components/DatePicker";
import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import ApiService from "../../../core/service/ApiService";
import Helper from "../../../core/Helper";
import FirmSelectList from "../../firm/FirmSelectList";
import UserSelectList from "../../user/UserSelectList";
import _ from "lodash";
import AppointmentTypeSelect from "../AppointmentTypeSelect";
import { DownOutlined, UpOutlined, SearchOutlined } from "@ant-design/icons";
import { matchesSearch } from "../../../core/utils/text-search";
import "./appointment-filter.css";
import ExcelButton from "../../../core/components/ExcelButton";

export default function AppointmentFilter({ view }) {
    const { queryModel, onFilterPageSort } = useDataGrid();
    const formRef = useRef();
    const methodsRef = useRef([])

    const [doctors, setDoctors] = useState([])
    const [institutions, setInstitutions] = useState([]);
    const [units, setUnits] = useState([])
    const [states, setStates] = useState([]);
    const [stateGroups, setStateGroups] = useState([]);
    const { firmSettings, hasPermission } = useUser();
    const [methods, setMethods] = useState([])
    const [searchText, setSearchText] = useState('')

    // Filtreleri açıp kapatmak için state — mobilde otomatik kapalı başla
    const screens = Grid.useBreakpoint();
    const isInitialMobile = useRef(typeof window !== 'undefined' && window.innerWidth < 768);
    const [expand, setExpand] = useState(!isInitialMobile.current);

    // Responsive Kolon Ayarları (xs: Mobil, sm: Tablet, md: Laptop, lg: Desktop)
    const colProps = {
        xs: 24,
        sm: 12,
        md: 8,
        lg: 6,
        xl: 4
    };

    // Form elemanları arası boşluğu azaltmak için stil
    const compactStyle = { marginBottom: 8 };

    useEffect(() => {
        formRef.current.setFieldsValue(queryModel.filter)
        setSearchText(queryModel.filter.searchText || '')
        Helper.UrlParams.updateUrlParam(queryModel.filter)
    }, [queryModel.updateKey])

    const load = async () => {
        return Promise.all([
            ApiService.Institution.GetInstitutionList(true).then(res => {
                if (res.success) setInstitutions(res.data)
            }),
            ApiService.Doctor.GetDoctorList({}).then(res => {
                if (res.success) setDoctors(res.data)
            }),
            ApiService.Unit.GetUnitList().then(res => {
                if (res.success) setUnits(res.data.map(x => ({ ...x, name: `${x?.firm?.name} - ${x.name}` })))
            }),
            ApiService.State.GetStateList().then(res => {
                if (res.success) {
                    setStates(res.data)
                    let groups = []
                    for (const item of res.data) {
                        const group = item.stateGroup
                        const exist = groups.find(x => x.id === group.id)
                        if (!exist) groups.push(group)
                    }
                    setStateGroups(groups)
                }
            }),
            ApiService.PaymentMethod.GetPaymentMethodList().then(res => {
                if (res.success) {
                    methodsRef.current = res.data
                    setMethods(res.data)
                }
            })
        ])
    }

    useEffect(() => { load() }, [])

    const onFinish = async (formData) => {
        let _states = []
        for (const st of (formData?.stateIdList || [])) {
            const group = stateGroups.find(x => x.name === st)
            if (group) {
                const values = states.filter(x => x.stateGroup?.name === st).map(x => x.id)
                _states = [..._states, ...values]
            } else {
                _states.push(st)
            }
        }
        onFilterPageSort({
            filter: { ...formData, stateIdList: _states }
        })
    }

    const exportExcel = async () => {
        const payload = {
            ...queryModel.filter,
            startDate: Helper.Date.beginOfDay(queryModel.filter.startDate),
            endDate: Helper.Date.endOfDay(queryModel.filter.endDate),
        };
        let res;
        if (view === 'entitlement') res = await ApiService.Entitlement.GetExcelEntitlements(payload);
        else if (view === 'appointment') res = await ApiService.Appointment.GetExcelAppointments(payload);
        else if (view === 'accounting') res = await ApiService.Accounting.GetExcelAppointmentAccounting(payload);

        if (res && res.success) {
            Helper.File.saveFile({ blobLink: res.data, fileName: res.fileName })
        }
    }

    const permissionMap = {
        entitlement: PERMISSIONS.ENTITLEMENT_EXPORT,
        appointment: PERMISSIONS.EXPORT_APPOINTMENTS,
        accounting: PERMISSIONS.EXPORT_APPOINTMENTS
    };
    const canExport = hasPermission(permissionMap[view]);

    const getMethods = () => {
        const group = _.groupBy(methods, (x) => x?.firm?.name)
        return Object.keys(group).map(key => (
            <Select.OptGroup key={key} label={key}>
                {group[key].map(x => (<Select.Option key={x.id} value={x.id}>{x.name}</Select.Option>))}
            </Select.OptGroup>
        ))
    }

    return (
        <Card styles={{ body: { padding: '12px' } }} style={{ backgroundColor: 'var(--surface-2)' }}>
            <Form
                layout={'vertical'}
                ref={formRef}
                onFinish={onFinish}
                size={'small'}
                initialValues={{
                    isDeleted: false, dateFilterType: 'appointmentDate', paymentStatus: 2, hasBalance: false
                }}
            >
                {/* --- HIZLI ARAMA ---
                    "YENİ! ... Randevuyu anında bulun" tanıtım cümlesi ve "tarih filtresi gerekmez" rozeti
                    kaldırıldı (özellik artık yeni değil); başlık olarak yalnız "Hızlı Arama" kaldı. */}
                <div className="quick-search">
                    <div className="quick-search-title">
                        <SearchOutlined />
                        <span>{t('Quick Search')}</span>
                    </div>
                    <Form.Item name='searchText' style={{ marginBottom: 0 }}>
                        <Input
                            prefix={<SearchOutlined style={{ color: 'var(--text-3)' }} />}
                            placeholder={t('Search by - Patient ID, Name, Phone, Email - Patient Note - Appointment Note, Anamnez, - Accession No etc.')}
                            allowClear
                            size="middle"
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </Form.Item>
                </div>

                {/* --- MAIN FILTERS (Always Visible) --- */}
                <Row gutter={[12, 0]}>
                    {/* Tarih Alanları */}
                    {view === 'entitlement' && (
                        <Col {...colProps}>
                            <Form.Item label={t('Date Filter Type')} name={'dateFilterType'} style={compactStyle}>
                                <Select style={{ width: '100%' }} disabled={!!searchText}>
                                    <Select.Option value={'appointmentDate'}>{t('Appointment Date')}</Select.Option>
                                    <Select.Option value={'entitlementConfirmDate'}>{t('Entitlement Confirm Date')}</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    )}

                    <Col {...colProps}>
                        <Form.Item label={t('Start Date')} name={'startDate'} style={compactStyle}>
                            <DatePicker style={{ width: '100%' }} disabled={!!searchText} format={'DD/MM/yyyy'} locale={Helper.Date.getLocale()} />
                        </Form.Item>
                    </Col>

                    <Col {...colProps}>
                        <Form.Item label={t('End Date')} name={'endDate'} style={compactStyle}>
                            <DatePicker style={{ width: '100%' }} disabled={!!searchText} format={'DD/MM/yyyy'} locale={Helper.Date.getLocale()} />
                        </Form.Item>
                    </Col>

                    <Col {...colProps}>
                        <Form.Item label={t('Quick Date')} style={compactStyle}>
                            <Select
                                disabled={!!searchText}
                                placeholder={t('Select')}
                                style={{ width: '100%' }}
                                onSelect={(val) => {
                                    const dates = Helper.Date.getDateFromNow(val);
                                    formRef.current.setFieldsValue({ startDate: dates.start, endDate: dates.end })
                                }}
                            >
                                <Select.Option value={'Today'}>{t('Today')}</Select.Option>
                                <Select.Option value={'Yesterday'}>{t('Yesterday')}</Select.Option>
                                <Select.Option value={'This Week'}>{t('This Week')}</Select.Option>
                                <Select.Option value={'This Month'}>{t('This Month')}</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col {...colProps}>
                        <Form.Item label={t('Has Balance')} name={'hasBalance'} valuePropName="checked" style={compactStyle}>
                            <Switch
                                onChange={(checked) => {
                                    const fields = { hasBalance: checked };
                                    formRef.current.setFieldsValue(fields);
                                    formRef.current.submit();
                                }}
                            />
                        </Form.Item>
                    </Col>

                    {/* Butonlar */}
                    <Col xs={24} sm={24} md={8} lg={6} xl={4} style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 8 }}>
                        <Button type={'primary'} htmlType={'submit'} style={{ flex: 1 }} icon={<SearchOutlined />}>
                            {t('Search')}
                        </Button>
                        <Button type={'default'} onClick={() => setExpand(!expand)} style={{ flex: 1 }}>
                            {expand ? <UpOutlined /> : <DownOutlined />} {expand ? t('Hide') : t('Filters')}
                        </Button>
                        {canExport && (
                            <ExcelButton onClick={exportExcel} style={{ flex: 1 }} />
                        )}
                    </Col>
                </Row>

                {/* --- SECONDARY FILTERS (Expandable) --- */}
                {expand && (
                    <>
                        <Divider dashed style={{ margin: '12px 0' }} />
                        <Row gutter={[12, 0]}>
                            <Col {...colProps}><Form.Item label={t('Firm')} name={'firmIdList'} style={compactStyle}><FirmSelectList style={{ width: '100%' }} allowClear mode={'multiple'} /></Form.Item></Col>
                            <Col {...colProps}><Form.Item label={t('Unit')} name={'unitIdList'} style={compactStyle}><Select allowClear style={{ width: '100%' }} showSearch mode={'tags'} fieldNames={{ label: 'name', value: 'id' }} options={units} filterOption={(input, option) => Helper.Text.normalizeText(JSON.stringify(option)).includes(Helper.Text.normalizeText(input))} /></Form.Item></Col>
                            <Col {...colProps}><Form.Item label={t('Institution')} name={'institutionIdList'} style={compactStyle}><Select allowClear showSearch mode={'multiple'} maxTagCount={'responsive'} style={{ width: '100%' }} fieldNames={{ label: 'name', value: 'id' }} options={institutions} filterOption={(input, option) => Helper.Text.normalizeText(JSON.stringify(option)).includes(Helper.Text.normalizeText(input))} /></Form.Item></Col>
                            <Col {...colProps}><Form.Item label={t('Appointment Type')} name={'appointmentTypeIdList'} style={compactStyle}><AppointmentTypeSelect style={{ width: '100%' }} mode={'multiple'} allowClear /></Form.Item></Col>
                            <Col {...colProps}><Form.Item label={t('Doctor')} name={'doctorId'} style={compactStyle}><Select allowClear showSearch style={{ width: '100%' }} fieldNames={{ label: 'name', value: 'id' }} options={doctors} filterOption={(input, option) => Helper.Text.normalizeText(JSON.stringify(option)).includes(Helper.Text.normalizeText(input))} /></Form.Item></Col>
                            <Col {...colProps}><Form.Item label={t('Patient Id')} name={'patientCustomId'} style={compactStyle}><Input style={{ width: '100%' }} /></Form.Item></Col>
                            <Col {...colProps}><Form.Item label={t('Patient Name')} name={'patientName'} style={compactStyle}><Input style={{ width: '100%' }} /></Form.Item></Col>
                            <Col {...colProps}><Form.Item label={t('Mother Name')} name={'patientMotherName'} style={compactStyle}><Input style={{ width: '100%' }} /></Form.Item></Col>
                            <Col {...colProps}><Form.Item label={t('Father Name')} name={'patientFatherName'} style={compactStyle}><Input style={{ width: '100%' }} /></Form.Item></Col>

                            {view !== 'entitlement' && (
                                <Col {...colProps}><Form.Item label={t('Payment Method')} name={'paymentMethodIdList'} style={compactStyle}><Select mode={'multiple'} allowClear showSearch style={{ width: '100%' }}>{getMethods()}</Select></Form.Item></Col>
                            )}

                            {hasPermission(PERMISSIONS.APPOINTMENT_VIEW_DELETE) && (
                                <Col {...colProps}><Form.Item label={t('Deleted')} name={'isDeleted'} style={compactStyle}><Select style={{ width: '100%' }}><Select.Option value={null}>{t('All')}</Select.Option><Select.Option value={false}>{t('Not Deleted')}</Select.Option><Select.Option value={true}>{t('Deleted')}</Select.Option></Select></Form.Item></Col>
                            )}

                            <Col {...colProps}>
                                <Form.Item label={t('State')} name={'stateIdList'} style={compactStyle}>
                                    <Select
                                        mode={'tags'}
                                        tagRender={(props) => {
                                            const { value, label, onClose } = props;
                                            const state = states.find(x => x.id === value);
                                            const group = stateGroups.find(x => x.name === value);

                                            // Fallback if not found (e.g. initial load or custom value)
                                            if (!state && !group) return <span className="ant-select-selection-item-content">{label}</span>;

                                            return (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 4, padding: '0 4px', border: '1px solid #d9d9d9', borderRadius: 2, background: '#fafafa' }}>
                                                    <div style={{ width: 10, height: 10, backgroundColor: state?.color || group?.color || '#ccc', border: '1px solid #000' }} />
                                                    <span style={{ marginRight: 4 }}>{label}</span>
                                                    <span onClick={onClose} style={{ cursor: 'pointer' }}>x</span>
                                                </div>
                                            );
                                        }}
                                        allowClear
                                        showSearch
                                        style={{ width: '100%' }}
                                        filterOption={(input, option) => matchesSearch(JSON.stringify(option), input)}
                                    >
                                        <Select.OptGroup label={t('Group')}>
                                            {stateGroups.map(x => (
                                                <Select.Option key={x.name} value={x.name}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <div style={{ width: 12, height: 12, backgroundColor: x.color, border: '1px solid #000' }} />
                                                        {t(x.name)}
                                                    </div>
                                                </Select.Option>
                                            ))}
                                        </Select.OptGroup>
                                        <Select.OptGroup value={'X'} label={t('Before Check-in')}>
                                            {states.filter(x => !x.checkInRequired).map(x => (
                                                <Select.Option key={x.id} value={x.id}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <div style={{ width: 12, height: 12, backgroundColor: x.color, border: '1px solid #000' }} />
                                                        {x.name}
                                                    </div>
                                                </Select.Option>
                                            ))}
                                        </Select.OptGroup>
                                        <Select.OptGroup value={'Y'} label={t('After Check-in')}>
                                            {states.filter(x => x.checkInRequired).map(x => (
                                                <Select.Option key={x.id} value={x.id}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <div style={{ width: 12, height: 12, backgroundColor: x.color, border: '1px solid #000' }} />
                                                        {x.name}
                                                    </div>
                                                </Select.Option>
                                            ))}
                                        </Select.OptGroup>
                                    </Select>
                                </Form.Item>
                            </Col>

                            {/* View'a özel diğer filtreler */}
                            {hasPermission(PERMISSIONS.FILTER_LOGS_BY_USER) && <Col {...colProps}><Form.Item label={t('Logs User')} name={'logsUserId'} style={compactStyle}><UserSelectList allowClear style={{ width: '100%' }} /></Form.Item></Col>}
                            {hasPermission(PERMISSIONS.ENTITLEMENT_RESPONSIBLE_FILTER) && <Col {...colProps}><Form.Item label={t('Responsible')} name={'responsibleUserId'} style={compactStyle}><UserSelectList allowClear style={{ width: '100%' }} /></Form.Item></Col>}

                            {view === 'accounting' && (
                                <>
                                    <Col {...colProps}><Form.Item label={t('Provision No')} name={'provisionNo'} style={compactStyle}><Input style={{ width: '100%' }} /></Form.Item></Col>
                                    <Col {...colProps}><Form.Item label={t('Payment Status')} name={'paymentStatus'} style={compactStyle}><Select style={{ width: '100%' }}><Select.Option value={null}>{t('All')}</Select.Option><Select.Option value={0}>{t('Balance')}</Select.Option><Select.Option value={1}>{t('Waiting')}</Select.Option><Select.Option value={2}>{t('Done')}</Select.Option><Select.Option value={3}>{t('Reject')}</Select.Option></Select></Form.Item></Col>
                                </>
                            )}

                            {view === 'entitlement' && (
                                <>
                                    <Col {...colProps}><Form.Item label={t('Entitlement Confirmation Status')} name={'entitlementConfirmStatusList'} style={compactStyle}><Select style={{ width: '100%' }} mode={'multiple'} allowClear><Select.Option value={null}>{t('All')}</Select.Option><Select.Option value={0}>{t('No Action')}</Select.Option><Select.Option value={1}>{t('Waiting')}</Select.Option><Select.Option value={2}>{t('Confirmed')}</Select.Option><Select.Option value={3}>{t('Canceled')}</Select.Option></Select></Form.Item></Col>
                                    <Col {...colProps}><Form.Item label={t('Entitlement Confirm User')} name={'entitlementConfirmUserId'} style={compactStyle}><UserSelectList showEntitlementConfirmUser={true} allowClear style={{ width: '100%' }} /></Form.Item></Col>
                                    <Col {...colProps}><Form.Item label={t('Entitlement')} name={'entitlementCompleted'} style={compactStyle}><Select style={{ width: '100%' }}><Select.Option value={null}>{t('All')}</Select.Option><Select.Option value={true}>{t('Completed')}</Select.Option><Select.Option value={false}>{t('Not Completed')}</Select.Option></Select></Form.Item></Col>
                                </>
                            )}
                        </Row>
                    </>
                )}
            </Form>
        </Card>
    )
}